<template>
  <div>
    <t-modal
      :header="$t('actions.insert_block')"
      xs
      header-low
      data-testid="dialog"
      @close="() => (showAddDialog = false)"
    >
      <template #dialog-body>
        <div class="px-3">
          <TTabsV2
            data-testid="dialog-tabs"
            :options="fillButtonGroupItems"
            @on-tab-change="onContentTabChanged"
          ></TTabsV2>
        </div>
        <div class="mb-5 mt-4 box-border px-5">
          <content-boxes-container
            v-if="selected === 'basic'"
            disable-notification
            @block-clicked="addBlock"
          ></content-boxes-container>
          <premade-container
            v-if="
              selected === 'premade' &&
              (premadeBlocksStore.premadeBlocks !== false ||
                premadeTemplates !== false)
            "
            style="width: 100%"
            clickable
            @premade-clicked="addPremadeBlock"
            @close-dialog="() => (showAddDialog = false)"
          ></premade-container>
          <saved-container
            v-if="
              selected === 'saved' &&
              (savedBlocksStore.savedBlocks ||
                savedBlocksStore.syncedSectionsEnabled)
            "
            style="width: 100%"
            clickable
            :section-index="savedSectionIndex"
            :to-empty="!isNotFromOptions"
            @saved-clicked="addSavedBlock"
            @close-dialog="closeDialog"
          ></saved-container>
        </div>
      </template>
    </t-modal>
  </div>
</template>

<script lang="ts" setup>
  import ContentBoxesContainer from "@/forms/components/ContentBoxesContainer/ContentBoxesContainer.vue";
  import SavedContainer from "@/forms/components/SavedContainer/SavedContainer.vue";
  import PremadeContainer from "@/forms/components/PremadeContainer/PremadeContainer.vue";

  import { useNotificationsStore } from "@/stores/notifications";
  import { useDialogsStore } from "@/stores/dialogs";
  import { useGeneralStore } from "@/stores/generalStore";
  import { useMjmlStore } from "@/stores/mjmlStore";
  import { useSavedBlocksStore } from "@/stores/savedBlocksStore";
  import { usePremadeBlocksStore } from "@/stores/premadeBlocksStore";
  import { usePremadeTemplatesStore } from "@/stores/premadeTemplatesStore";
  import { TopolBlockClickedEvent } from "@/schemas/contentBlock/contentBlockScheme";

  import { TModal, TTabsV2 } from "@topol/twig-ui";

  import { ref, computed } from "vue";
  import { useI18n } from "vue-i18n";

  const { t } = useI18n();

  const notificationsStore = useNotificationsStore();
  const dialogsStore = useDialogsStore();
  const generalStore = useGeneralStore();
  const mjmlStore = useMjmlStore();
  const savedBlocksStore = useSavedBlocksStore();
  const premadeBlocksStore = usePremadeBlocksStore();
  const { premadeTemplates } = usePremadeTemplatesStore();

  const selectedTab = ref("blocks");
  const selected = ref("basic");

  type ButtonGroupItem = {
    name: string;
    value: string;
    disabled?: boolean;
    tooltip?: string;
  };

  type PremadeBlockEvent = {
    index: number;
    name: number;
  };

  const direction = computed(() => {
    return dialogsStore.addBlockDialog.addDirection;
  });

  const options = computed(() => {
    return dialogsStore.addBlockDialog.options;
  });

  const showAddDialog = computed({
    get() {
      return dialogsStore.addBlockDialog.opened;
    },
    set() {
      dialogsStore.closeBlockDialog();
    },
  });

  const isNotFromOptions = computed(() => {
    return (
      (options.value === null || options.value === undefined) &&
      sectionId.value !== undefined
    );
  });

  const sectionId = computed(() => {
    return mjmlStore.selectedBlock?.sectionIndex;
  });
  const columnId = computed(() => {
    return mjmlStore.selectedBlock?.columnIndex;
  });
  const blockId = computed(() => {
    return mjmlStore.selectedBlock?.blockIndex;
  });

  // The saved-sections panel splices straight at this index, so the "add below"
  // offset is resolved here, where the dialog owns its own direction, rather
  // than in the panel — which would have to guess which dialog it sits in.
  const savedSectionIndex = computed(() => {
    if (!isNotFromOptions.value || sectionId.value === undefined) {
      return options.value?.sectionIndex;
    }
    return direction.value === "bottom" ? sectionId.value + 1 : sectionId.value;
  });

  const fillButtonGroupItems = computed(() => {
    const buttonGroupItems: ButtonGroupItem[] = [];
    buttonGroupItems.push({
      name: t("blocks.empty_content"),
      value: "basic",
      tooltip: "",
    });

    //add disabled buttons for standalone version
    if (generalStore.type === "standalone") {
      buttonGroupItems.push({
        name: "Pre-made",
        value: "premade",
        disabled: true,
        tooltip: "Only PRO",
      });

      buttonGroupItems.push({
        name: "Saved",
        value: "saved",
        disabled: true,
        tooltip: "Only PRO",
      });
    } else {
      //Add premade blocks to button group
      if (premadeBlocksStore.premadeBlocks !== false) {
        buttonGroupItems.push({
          name: t("blocks.premade_content"),
          value: "premade",
          tooltip: "",
        });
      }

      //Add saved blocks to button group
      if (
        savedBlocksStore.savedBlocks !== false ||
        savedBlocksStore.syncedSectionsEnabled
      ) {
        buttonGroupItems.push({
          name: t("blocks.saved_content"),
          value: "saved",
          tooltip: "",
        });
      }
    }
    return buttonGroupItems;
  });

  const carouselCanBeAdded = (sectionIndex: number): boolean => {
    const section = mjmlStore.getSelectedSectionByIndex(sectionIndex);

    if (section.children.length > 1) {
      notificationsStore.createNotification({
        type: "error",
        text: t("mjml.carousel_one_column_error_message"),
      });
      return false;
    }

    return true;
  };

  function addBlock(typeObject: TopolBlockClickedEvent) {
    //Automatically resolve indexes based on active block
    if (options.value === null || options.value === undefined) {
      if (typeObject.type === "carousel" && sectionId.value !== undefined) {
        if (!carouselCanBeAdded(sectionId.value)) {
          return;
        }
      }
      let newBlockIndex = blockId.value;

      if (direction.value === "bottom" && newBlockIndex !== undefined) {
        newBlockIndex++;
      }

      mjmlStore.addBlock({
        sectionIndex: sectionId.value,
        columnIndex: columnId.value,
        blockIndex: newBlockIndex,
        type: typeObject.type,
        customBlockData: typeObject.customBlockData,
        byClick: true,
      });
    } else {
      if (typeObject.type === "carousel") {
        if (!carouselCanBeAdded(options.value.sectionIndex)) {
          return;
        }
      }

      mjmlStore.addBlock({
        sectionIndex: options.value.sectionIndex,
        columnIndex: options.value.columnIndex,
        blockIndex: options.value.blockIndex,
        type: typeObject.type,
        customBlockData: typeObject.customBlockData,
        byClick: true,
      });
    }

    selectedTab.value = "blocks";
    dialogsStore.closeBlockDialog();
  }

  function addPremadeBlock(premadeBlock: PremadeBlockEvent) {
    if (options.value === null || options.value === undefined) {
      let newSectionId = sectionId.value;

      // Not `!newSectionId`: section index 0 is falsy, so the first structure
      // in the template used to swallow the click without inserting anything.
      if (newSectionId === undefined) {
        return;
      }

      //premade block behaves as a strucutre so we increment sectionId here
      if (direction.value === "bottom") {
        newSectionId++;
      }

      premadeBlocksStore.addPremadeBlock({
        sectionIndex: newSectionId,
        blockIndex: premadeBlock.index,
        blockType: premadeBlock.name,
        byClick: true,
      });
    } else {
      premadeBlocksStore.addPremadeBlock({
        sectionIndex: options.value.sectionIndex,
        blockIndex: premadeBlock.index,
        blockType: premadeBlock.name,
        emptyBlock: true,
      });
    }

    selectedTab.value = "blocks";
    dialogsStore.closeBlockDialog();
  }

  function addSavedBlock(blockIndex: number) {
    if (
      (options.value === null || options.value === undefined) &&
      sectionId.value !== undefined
    ) {
      savedBlocksStore.addSavedBlock({
        sectionIndex: sectionId.value,
        blockIndex: blockIndex,
        categoryIndex: null,
        byClick: true,
      });
    } else {
      savedBlocksStore.addSavedBlock({
        sectionIndex: options.value.sectionIndex,
        blockIndex: blockIndex,
        categoryIndex: null,
        emptyBlock: true,
      });
    }

    selectedTab.value = "blocks";
    dialogsStore.closeBlockDialog();
  }

  function closeDialog() {
    selectedTab.value = "blocks";
    dialogsStore.closeBlockDialog();
  }

  function onContentTabChanged(index: number) {
    selected.value = fillButtonGroupItems.value[index].value;
  }
</script>

<style scoped lang="scss">
  .button-group-container {
    display: flex;
    justify-content: center;
    padding-bottom: 24px;
  }

  .content-container {
    display: flex;
    justify-content: center;
    padding-bottom: 32px;
    min-width: 396px;
  }
</style>

<style>
  .dialog-bg-left {
    max-width: 390px;
    margin: 0px 0px 65px calc(50%);
  }

  .dialog-bg-right {
    max-width: 390px;
    margin: 0px 0px 0px calc(30%);
  }
</style>
